import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useFetchGet } from '../../utils/fetch/useFetchGet';
import { pathsApi } from '../../router/paths';
import i18n from '../../utils/i18n';
import Body from '../../ui/Body/body';
import GridRow from '../../ui/GridRow';
import { ICategoryFilter, ICategoryFilterGroup } from './types';
import GridCol from '../../ui/GridCol';
import Details from '../../ui/Details/details';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { ButtonVariationEnum } from '../../ui/Button/types';
import Popup from '../../ui/Popup';
import Input from '../../ui/Input';
import { PopupSize } from '../../ui/Popup/Popup';
import { FetchApi } from '../../utils/fetch/fetchApi';

const CategoryFilters = () => {
    const { data, setData } = useFetchGet<Array<ICategoryFilterGroup>>({ path: pathsApi.categoryFilters });
    const [isNewOpen, handleNewOpen] = useState<false | number>(false);
    const [isNewGroup, handleOpenNewGroup] = useState<boolean>(false);
    const [isLoading, setLoading] = useState(false);

    const { control, reset, handleSubmit } = useForm<ICategoryFilter>();
    const { control: controlGroup, reset: resetGroup, handleSubmit: handleSubmitGroup } = useForm<{ name: string }>();

    const handleSave = handleSubmit(async (data) => {
        setLoading(true);
        const response = await FetchApi.post<Array<ICategoryFilterGroup>>(pathsApi.categoryFiltersSaveItem, {
            name: data.name,
            group: isNewOpen,
        });

        if (response) {
            setData(response);
            handleNewOpen(false);
            reset();
        }
        setLoading(false);
    });

    const handleSaveGroup = handleSubmitGroup(async (data) => {
        setLoading(true);
        const response = await FetchApi.post<Array<ICategoryFilterGroup>>(pathsApi.categoryFiltersSaveGroup, {
            name: data.name,
        });

        if (response) {
            setData(response);
            handleOpenNewGroup(false);
            resetGroup();
        }
        setLoading(false);
    });

    const dataMemo = useMemo(() => {
        return data?.map((group) => (
            <Details key={group.id} label={group.name}>
                <GridRow gutter={4}>
                    <GridCol col={12}>
                        <GridRow isFlex gutter={8}>
                            <Button
                                isSmall
                                variation={ButtonVariationEnum.SOFT}
                                onClick={() => handleNewOpen(group.id)}
                            >
                                {i18n.category.newCategoryFilterItem}
                            </Button>
                        </GridRow>
                    </GridCol>
                    {group.items
                        ?.sort((a, b) => new Intl.Collator('en').compare(a.name, b.name))
                        .map((item) => (
                            <GridCol col={3} key={item.id}>
                                <Card>{item.name}</Card>
                            </GridCol>
                        ))}
                </GridRow>
            </Details>
        ));
    }, [data]);

    const handleNewClose = () => {
        reset();
        handleNewOpen(false);
    };

    return (
        <Body title={i18n.menu.categoryFilters} isLoading={!data}>
            <GridRow column gutter={8}>
                <div>
                    <Button
                        onClick={() => {
                            handleOpenNewGroup(true);
                        }}
                        variation={ButtonVariationEnum.SOFT}
                    >
                        {i18n.category.newCategoryFilter}
                    </Button>
                </div>
                <div>{dataMemo}</div>
            </GridRow>
            {isNewOpen !== false && (
                <Popup size={PopupSize.s} close={handleNewClose} title={i18n.category.newCategoryFilterItem}>
                    <GridRow gutter={8} column>
                        <Controller
                            control={control}
                            name={'name'}
                            defaultValue={''}
                            rules={{ required: true }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <Input
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    errorMessage={error?.message}
                                    label={i18n.general.name}
                                />
                            )}
                        />
                        <Button onClick={handleSave} isLoading={isLoading}>
                            {i18n.general.save}
                        </Button>
                    </GridRow>
                </Popup>
            )}
            {isNewGroup && (
                <Popup size={PopupSize.s} close={handleNewClose} title={i18n.category.newCategoryFilter}>
                    <GridRow gutter={8} column>
                        <Controller
                            control={controlGroup}
                            name={'name'}
                            defaultValue={''}
                            rules={{ required: true }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <Input
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    errorMessage={error?.message}
                                    label={i18n.general.name}
                                />
                            )}
                        />
                        <Button onClick={handleSaveGroup} isLoading={isLoading}>
                            {i18n.general.save}
                        </Button>
                    </GridRow>
                </Popup>
            )}
        </Body>
    );
};

export default CategoryFilters;
